import React, { useState, useEffect } from "react";
import { 
  Box, TextField, MenuItem, Select, FormControl, InputLabel, Button, 
  Typography, Grid, Switch, IconButton, CircularProgress
} from "@mui/material";
import { PlayArrow, Stop, PowerSettingsNew, Add, Remove, ListAlt  } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import axiosInstance from "../axiosInstance";

const apiUrl = process.env.REACT_APP_API_URL;

const DropboxConnectionViewPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [dropboxLink, setDropboxLink] = useState("");
  const [vpcCol, setVpcCol] = useState("");
  const [stockCol, setStockCol] = useState("");
  const [costCol, setCostCol] = useState("");

  const [scheduleType, setScheduleType] = useState("daily");
  const [minute, setMinute] = useState("0");
  const [hour, setHour] = useState("0");
  const [dayOfMonth, setDayOfMonth] = useState("*");
  const [month, setMonth] = useState("*");
  const [dayOfWeek, setDayOfWeek] = useState("*");

  const [enableStockSyntax, setEnableStockSyntax] = useState(false);
  const [stockSyntax, setStockSyntax] = useState([{ label: "", value: "" }]);

  const [enableStockAdjustment, setEnableStockAdjustment] = useState(false);
  const [stockAdjustment, setStockAdjustment] = useState("");

    const [enableColumnSyntax, setEnableColumnSyntax] = useState(false);
  const [columnSyntax, setColumnSyntax] = useState([{ type: "Stock", operation: "+", syntax: "" }]);

  const [enableColumnFilter, setEnableColumnFilter] = useState(false);
  const [columnFilter, setColumnFilter] = useState([{ type: "Stock", column: "", text: "" }]);

  const [enableGreenStock, setEnableGreenStock] = useState(false);
const [mathEquation, setMathEquation] = useState("");
  const navigate = useNavigate();
  // Fetch existing data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          toast.error("Unauthorized. Please login again.");
          return;
        }

        const response = await axiosInstance.get(`${apiUrl}/dropbox-cron/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = response.data.cron;
        setDropboxLink(data.dropboxLink || "");
        setVpcCol(data.vpcCol || "");
        setStockCol(data.stockCol || "");
        setCostCol(data.costCol || "");
        setEnableStockSyntax(data.enableStockSyntax || false);
        setStockSyntax(data.stockSyntax ? Object.entries(JSON.parse(data.stockSyntax)).map(([label, value]) => ({ label, value })) : [{ label: "", value: "" }]);
        setEnableStockAdjustment(data.enableStockAdjustment || false);
        setStockAdjustment(data.stockAdjustment || "");

        setEnableColumnSyntax(data.enableColumnSyntax || false);
        setColumnSyntax(
          data.columnSyntax
            ? JSON.parse(data.columnSyntax)
            : [{ type: "Stock", operation: "+", syntax: "" }]
        );

        setEnableColumnFilter(data.enableColumnFilter || false);
        setColumnFilter(
          data.columnFilter
            ? JSON.parse(data.columnFilter)
            : [{ type: "Stock", column: "", text: "" }]
        );

        setEnableGreenStock(data.enableGreenStock || false);
        setMathEquation(data.mathEquation || "");

        setScheduleType(data.scheduleType || daily);
        // Parse cron expression
        const [m, h, dom, mon, dow] = data.cronExpression.split(" ");
        setMinute(m || "0");
        setHour(h || "0");
        setDayOfMonth(dom || "*");
        setMonth(mon || "*");
        setDayOfWeek(dow || "*");

        setLoading(false);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load data.");
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleStockSyntaxChange = (index, field, newValue) => {
    const updatedStockSyntax = [...stockSyntax];
    updatedStockSyntax[index][field] = newValue;
    setStockSyntax(updatedStockSyntax);
  };

  const addStockSyntax = () => {
    setStockSyntax([...stockSyntax, { label: "", value: "" }]);
  };

  const removeStockSyntax = (index) => {
    const updatedStockSyntax = [...stockSyntax];
    updatedStockSyntax.splice(index, 1);
    setStockSyntax(updatedStockSyntax);
  };

  const handleColumnSyntaxChange = (index, field, newValue) => {
    const updatedColumnSyntax = [...columnSyntax];
    updatedColumnSyntax[index][field] = newValue;
    setColumnSyntax(updatedColumnSyntax);
  };
  
  const addColumnSyntax = () => {
    setColumnSyntax([...columnSyntax, { type: "Stock", operation: "+", syntax: "" }]);
  };
  
  const removeColumnSyntax = (index) => {
    const updatedColumnSyntax = [...columnSyntax];
    updatedColumnSyntax.splice(index, 1);
    setColumnSyntax(updatedColumnSyntax);
  };

  const handleColumnFilterChange = (index, field, newValue) => {
    const updatedColumnFilter = [...columnFilter];
    updatedColumnFilter[index][field] = newValue;
    setColumnFilter(updatedColumnFilter);
  };
  
  const addColumnFilter = () => {
    setColumnFilter([...columnFilter, { type: "Stock", column: "", text: "" }]);
  };
  
  const removeColumnFilter = (index) => {
    const updatedColumnFilter = [...columnFilter];
    updatedColumnFilter.splice(index, 1);
    setColumnFilter(updatedColumnFilter);
  };
  

  const handleUpdate = async () => {
    if (!dropboxLink) {
      toast.error("Dropbox link is required!");
      return;
    }

    const cronExpression = `${minute} ${hour} ${dayOfMonth} ${month} ${dayOfWeek}`;

    const stockSyntaxJson = enableStockSyntax 
    ? JSON.stringify(Object.fromEntries(stockSyntax.map(({ label, value }) => [label, value]))) 
    : null;

    const columnSyntaxJson = enableColumnSyntax 
    ? JSON.stringify(columnSyntax) 
    : null;

    const columnFilterJson = enableColumnFilter 
    ? JSON.stringify(columnFilter) 
    : null;

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        toast.error("Unauthorized. Please login again.");
        return;
      }

      const response = await axiosInstance.put(
        `${apiUrl}/dropbox-cron/${id}`,
        { dropboxLink, cronExpression, vpcCol, stockCol, costCol, enableStockSyntax, stockSyntax: stockSyntaxJson, enableStockAdjustment, stockAdjustment, columnSyntax: columnSyntaxJson, enableColumnSyntax , columnFilter: columnFilterJson, enableColumnFilter, enableGreenStock, mathEquation },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message || "Cron job updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update cron job.");
    }
  };

   // Handle "Run Now" API
   const handleRunNow = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        toast.error("Unauthorized. Please login again.");
        return;
      }

      await axiosInstance.post(`${apiUrl}/dropbox-cron-run-now/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Dropbox cron job Ran successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to start cron job.");
    }
  };

  // Handle "Stop" API
  const handleStop = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        toast.error("Unauthorized. Please login again.");
        return;
      }

      await axiosInstance.post(`${apiUrl}/dropbox-cron-stop/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Dropbox cron job stopped successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to stop cron job.");
    }
  };

    // Handle "Start" API
    const handleStart = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          toast.error("Unauthorized. Please login again.");
          return;
        }
  
        await axiosInstance.post(`${apiUrl}/dropbox-cron-start/${id}`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
  
        toast.success("Dropbox cron job Started successfully!");
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to stop cron job.");
      }
    };


  if (loading) {
    return (
      <Box sx={{ textAlign: "center", mt: 5 }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", mt: 2, p: 4, bgcolor: "#fff", borderRadius: 2, boxShadow: 3 }}>
      {/* Header with "Run Now" & "Stop" Buttons */}
      <Box display="flex" justifyContent="flex-end" alignItems="center">
  <Button
    variant="contained"
    color="primary"
    startIcon={<ListAlt />}
    sx={{ mr: 2 }}
    onClick={() => navigate(`/dropbox-logs/${id}`)}
  >
    View Logs
  </Button>
</Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" mt={4}>
        <Typography variant="h4">Edit Connection</Typography>
        <Box>
          <Button
            variant="contained"
            color="success"
            startIcon={<PlayArrow />}
            sx={{ mr: 2 }}
            onClick={handleRunNow}
          >
            Run Now
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<PowerSettingsNew />}
            sx={{ mr: 2 }}
            onClick={handleStart}
          >
            Start
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<Stop />}
            onClick={handleStop}
          >
            Stop
          </Button>
        </Box>
      </Box>

      <TextField fullWidth label="Link" variant="outlined" margin="normal" value={dropboxLink} onChange={(e) => setDropboxLink(e.target.value)} />
      <TextField fullWidth label="VPC Column" variant="outlined" margin="normal" value={vpcCol} onChange={(e) => setVpcCol(e.target.value)} />
      <TextField fullWidth label="Stock Column" variant="outlined" margin="normal" value={stockCol} onChange={(e) => setStockCol(e.target.value)} />
      <TextField fullWidth label="Cost Column" variant="outlined" margin="normal" value={costCol} onChange={(e) => setCostCol(e.target.value)} />

   <FormControl fullWidth margin="normal">
        <InputLabel>Schedule Type</InputLabel>
        <Select value={scheduleType} onChange={(e) => setScheduleType(e.target.value)}>
          <MenuItem value="hourly">Hourly</MenuItem>
          <MenuItem value="daily">Daily</MenuItem>
          <MenuItem value="twicePerDay">Twice Per Day</MenuItem>
          <MenuItem value="weekly">Weekly</MenuItem>
          <MenuItem value="monthly">Monthly</MenuItem>
          <MenuItem value="custom">Custom (Advanced)</MenuItem>
        </Select>
      </FormControl>
        {/* Dynamic Cron Expression Fields */}
           <Grid container spacing={2} sx={{ mt: 2 }}>
             {/* Hourly: Only show minute */}
             {["hourly"].includes(scheduleType) && (
               <Grid item xs={6}>
                 <TextField label="Minute" value={minute} onChange={(e) => setMinute(e.target.value)} fullWidth />
               </Grid>
             )}
     
             {/* Daily, Twice Per Day: Show hour & minute */}
             {["daily", "twicePerDay"].includes(scheduleType) && (
               <>
                 <Grid item xs={6}>
                   <TextField label="Hour" value={hour} onChange={(e) => setHour(e.target.value)} fullWidth />
                 </Grid>
                 <Grid item xs={6}>
                   <TextField label="Minute" value={minute} onChange={(e) => setMinute(e.target.value)} fullWidth />
                 </Grid>
               </>
             )}
     
             {/* Weekly: Show hour, minute, and day of week */}
             {scheduleType === "weekly" && (
               <>
                 <Grid item xs={4}>
                   <TextField label="Hour" value={hour} onChange={(e) => setHour(e.target.value)} fullWidth />
                 </Grid>
                 <Grid item xs={4}>
                   <TextField label="Minute" value={minute} onChange={(e) => setMinute(e.target.value)} fullWidth />
                 </Grid>
                 <Grid item xs={4}>
                   <TextField label="Day of Week (0-6)" value={dayOfWeek} onChange={(e) => setDayOfWeek(e.target.value)} fullWidth />
                 </Grid>
               </>
             )}
     
             {/* Monthly: Show hour, minute, and day of month */}
             {scheduleType === "monthly" && (
               <>
                 <Grid item xs={4}>
                   <TextField label="Hour" value={hour} onChange={(e) => setHour(e.target.value)} fullWidth />
                 </Grid>
                 <Grid item xs={4}>
                   <TextField label="Minute" value={minute} onChange={(e) => setMinute(e.target.value)} fullWidth />
                 </Grid>
                 <Grid item xs={4}>
                   <TextField label="Day of Month (1-31)" value={dayOfMonth} onChange={(e) => setDayOfMonth(e.target.value)} fullWidth />
                 </Grid>
               </>
             )}
     
             {/* Custom: Show all fields */}
             {scheduleType === "custom" && (
               <>
                 <Grid item xs={4}>
                   <TextField label="Minute" value={minute} onChange={(e) => setMinute(e.target.value)} fullWidth />
                 </Grid>
                 <Grid item xs={4}>
                   <TextField label="Hour" value={hour} onChange={(e) => setHour(e.target.value)} fullWidth />
                 </Grid>
                 <Grid item xs={4}>
                   <TextField label="Day of Month" value={dayOfMonth} onChange={(e) => setDayOfMonth(e.target.value)} fullWidth />
                 </Grid>
                 <Grid item xs={6}>
                   <TextField label="Month" value={month} onChange={(e) => setMonth(e.target.value)} fullWidth />
                 </Grid>
                 <Grid item xs={6}>
                   <TextField label="Day of Week" value={dayOfWeek} onChange={(e) => setDayOfWeek(e.target.value)} fullWidth />
                 </Grid>
               </>
             )}
           </Grid>

      {/* Stock Syntax Toggle & Fields */}
      <Box display="flex" alignItems="center" sx={{ mt: 2 }}>
        <Typography variant="h6">Enable Stock Syntax</Typography>
        <Switch checked={enableStockSyntax} onChange={(e) => setEnableStockSyntax(e.target.checked)} />
      </Box>

       {enableStockSyntax && (
              <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 2 }}>
                <Typography variant="h6">Stock Syntax Mapping</Typography>
      
                {stockSyntax.map((item, index) => (
                  <Grid container spacing={2} alignItems="center" key={index} sx={{ mt: 1 }}>
                    <Grid item xs={5}>
                      <TextField
                        label="Stock Label"
                        value={item.label}
                        onChange={(e) => handleStockSyntaxChange(index, "label", e.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <TextField
                        label="Quantity"
                        type="number"
                        value={item.value}
                        onChange={(e) => handleStockSyntaxChange(index, "value", e.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={2}>
                      {index > 0 && (
                        <IconButton onClick={() => removeStockSyntax(index)} color="error">
                          <Remove />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ))}
      
                <Button
                  startIcon={<Add />}
                  onClick={addStockSyntax}
                  sx={{ mt: 2 }}
                  variant="contained"
                  color="secondary"
                >
                  Add More
                </Button>
              </Box>
            )}

       {/* Toggle for Stock Adjustment */}
             <Box display="flex" alignItems="center" sx={{ mt: 2 }}>
              <Typography variant="h6">Enable Stock Adjustment</Typography>
              <Switch checked={enableStockAdjustment} onChange={(e) => setEnableStockAdjustment(e.target.checked)} />
            </Box>
      
            {enableStockAdjustment && (
              <TextField fullWidth label="Stock Adjustment Values" variant="outlined" margin="normal" value={stockAdjustment} onChange={(e) => setStockAdjustment(e.target.value)} />
            )}

               {/* Toggle for Column Syntax */}
            <Box display="flex" alignItems="center" sx={{ mt: 2 }}>
            <Typography variant="h6">Enable Column Syntax</Typography>
            <Switch checked={enableColumnSyntax} onChange={(e) => setEnableColumnSyntax(e.target.checked)} />
            </Box>
            
            {/* Column Syntax Section */}
            {enableColumnSyntax && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 2 }}>
              <Typography variant="h6">Column Syntax Mapping</Typography>
              {columnSyntax.map((item, index) => (
                <Grid container spacing={2} alignItems="center" key={index} sx={{ mt: 1 }}>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={item.type}
                        onChange={(e) => handleColumnSyntaxChange(index, "type", e.target.value)}
                      >
                        <MenuItem value="Stock">Stock</MenuItem>
                        <MenuItem value="VPC">VPC</MenuItem>
                        <MenuItem value="Cost">Cost</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={3}>
                    <FormControl fullWidth>
                      <InputLabel>Operation</InputLabel>
                      <Select
                        value={item.operation}
                        onChange={(e) => handleColumnSyntaxChange(index, "operation", e.target.value)}
                        disabled={item.type === "VPC"}
                      >
                        <MenuItem value="+">+</MenuItem>
                        <MenuItem value="-" disabled={item.type === "VPC"}>-</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Syntax"
                      value={item.syntax}
                      onChange={(e) => handleColumnSyntaxChange(index, "syntax", e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={1}>
                    {index > 0 && (
                      <IconButton onClick={() => removeColumnSyntax(index)} color="error">
                        <Remove />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}

              
              <Button startIcon={<Add />} onClick={addColumnSyntax} sx={{ mt: 2 }} variant="contained" color="secondary">
                Add More
              </Button>
            </Box>
            )}
            

            <Box display="flex" alignItems="center" sx={{ mt: 2 }}>
              <Typography variant="h6">Enable Column Filter</Typography>
              <Switch checked={enableColumnFilter} onChange={(e) => setEnableColumnFilter(e.target.checked)} />
            </Box>
            
            {enableColumnFilter && (
              <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 2 }}>
                <Typography variant="h6">Column Filter</Typography>
                {columnFilter.map((item, index) => (
                  <Grid container spacing={2} alignItems="center" key={index} sx={{ mt: 1 }}>
                    <Grid item xs={4}>
                      <FormControl fullWidth>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={item.type}
                          onChange={(e) => handleColumnFilterChange(index, "type", e.target.value)}
                        >
                          <MenuItem value="Stock">Stock</MenuItem>
                          <MenuItem value="VPC">VPC</MenuItem>
                          <MenuItem value="Cost">Cost</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label="Column"
                        value={item.column}
                        onChange={(e) => handleColumnFilterChange(index, "column", e.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        label="Text"
                        value={item.text}
                        onChange={(e) => handleColumnFilterChange(index, "text", e.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={1}>
                      {index > 0 && (
                        <IconButton onClick={() => removeColumnFilter(index)} color="error">
                          <Remove />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ))}
                <Button startIcon={<Add />} onClick={addColumnFilter} sx={{ mt: 2 }} variant="contained" color="secondary">
                  Add More
                </Button>
              </Box>
            )}
            
            
            <Box display="flex" alignItems="center" sx={{ mt: 2 }}>
              <Typography variant="h6">Enable Green Stock</Typography>
              <Switch checked={enableGreenStock} onChange={(e) => setEnableGreenStock(e.target.checked)} />
            </Box>

            <TextField
                    fullWidth
                    label="Enter Math Equation for Cost"
                    variant="outlined"
                    margin="normal"
                    value={mathEquation}
                    onChange={(e) => setMathEquation(e.target.value)}
                  />
            

      {/* Update Button */}
      <Button variant="contained" color="primary" fullWidth sx={{ mt: 3 }} onClick={handleUpdate}>
        Update & Save
      </Button>
    </Box>
  );
};

export default DropboxConnectionViewPage;
